import React, { useEffect, useState } from "react";
import { Box, Button, Card, Collapse, InputAdornment, Link, MenuItem, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { MonthSettingsInput, ReportData } from "../types";
import { fmt } from "../utils/format";

interface Props {
  report: ReportData;
  saving: boolean;
  onSave: (settings: MonthSettingsInput) => void;
}

const toInput = (n: number | null | undefined) => (n === null || n === undefined ? "" : String(n));
const toNumber = (s: string) => (s.trim() === "" ? null : Number(s));

/**
 * Month-end inputs: actual balance, cash holder, mortgage category and
 * override, and (rarely) an opening balance. Saved together; the backend
 * recalculates and returns the whole report.
 */
const MonthSettingsCard: React.FC<Props> = ({ report, saving, onSave }) => {
  const monthly = report.monthly!;
  const { mortgage, partnerShare, month } = monthly;
  const editable = month.status !== "closed" && monthly.permissions.canEdit;

  const initial = {
    actual: toInput(monthly.reconciliation.actual),
    cashHolder: partnerShare.cashHolderId ? String(partnerShare.cashHolderId) : "",
    category: mortgage.categoryId ? String(mortgage.categoryId) : "",
    override: toInput(mortgage.overrideAmount),
    opening: toInput(month.openingBalance),
  };
  const [form, setForm] = useState(initial);
  const [showOpening, setShowOpening] = useState(month.openingBalance !== null);

  // Reset when another month (or a refreshed report) is shown.
  useEffect(() => {
    setForm(initial);
    setShowOpening(month.openingBalance !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const invalidNumber = [form.actual, form.override, form.opening].some((v) => v.trim() !== "" && Number.isNaN(Number(v)));

  const save = () => {
    const settings: MonthSettingsInput = {
      actual_balance: toNumber(form.actual),
      cash_holder_user_id: form.cashHolder ? Number(form.cashHolder) : null,
      mortgage_override_amount: toNumber(form.override),
      opening_balance: toNumber(form.opening),
    };
    // Only send the category when it was chosen explicitly, so an
    // auto-detected "Mortgage" category stays auto-detected.
    if (form.category !== initial.category || !mortgage.categoryAuto) {
      settings.mortgage_category_id = form.category ? Number(form.category) : null;
    }
    onSave(settings);
  };

  const money = { startAdornment: <InputAdornment position="start">$</InputAdornment> };

  return (
    <Card elevation={2} sx={{ borderRadius: 2, p: 2, height: "100%" }}>
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        Month Settings
      </Typography>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1.5}>
        <TextField
          size="small"
          label="Actual Balance (bank + cash)"
          value={form.actual}
          onChange={set("actual")}
          disabled={!editable}
          InputProps={money}
          inputProps={{ inputMode: "decimal" }}
          helperText={`Calculated: ${fmt(monthly.reconciliation.calculated)}`}
        />
        {partnerShare.applicable ? (
          <TextField
            select
            size="small"
            label="Cash Holder"
            value={form.cashHolder}
            onChange={set("cashHolder")}
            disabled={!editable}
            helperText="Cash on hand counts against this partner"
          >
            <MenuItem value="">
              <em>Not selected</em>
            </MenuItem>
            {partnerShare.partners.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Box />
        )}
        <TextField
          select
          size="small"
          label="Mortgage Category"
          value={form.category}
          onChange={set("category")}
          disabled={!editable}
          helperText={mortgage.categoryAuto ? "Auto-detected" : " "}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {mortgage.categoryOptions.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Mortgage Override Amount"
          value={form.override}
          onChange={set("override")}
          disabled={!editable}
          InputProps={money}
          inputProps={{ inputMode: "decimal" }}
          helperText="Optional — leave blank to use the category total"
        />
      </Box>

      <Box sx={{ mt: 1, p: 1.25, borderRadius: 1, backgroundColor: "#f5f6f8" }}>
        <Typography variant="body2">
          Mortgage Category Total: <b>{mortgage.categoryTotal === null ? "—" : fmt(mortgage.categoryTotal)}</b>
        </Typography>
        <Typography variant="body2">
          Override: <b>{mortgage.overrideAmount === null ? "—" : fmt(mortgage.overrideAmount)}</b>
        </Typography>
        <Typography variant="body2">
          Effective Mortgage: <b>{mortgage.effectiveAmount === null ? "—" : fmt(mortgage.effectiveAmount)}</b>
        </Typography>
      </Box>

      <Box mt={1}>
        {!showOpening ? (
          editable && (
            <Link component="button" variant="caption" onClick={() => setShowOpening(true)}>
              Set an opening balance (first reporting month only)
            </Link>
          )
        ) : null}
        <Collapse in={showOpening}>
          <TextField
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            label="Opening Balance"
            value={form.opening}
            onChange={set("opening")}
            disabled={!editable}
            InputProps={money}
            helperText="Replaces the calculated carry forward for this month. Leave blank to derive it from history."
          />
        </Collapse>
      </Box>

      {editable && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={!dirty || saving || invalidNumber}>
            {saving ? "Saving…" : "Save Month Settings"}
          </Button>
        </Box>
      )}
      {month.status === "closed" && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          This month is closed. Reopen it to change settings.
        </Typography>
      )}
      {month.status !== "closed" && !monthly.permissions.canEdit && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          You can view this month but not change its settings.
        </Typography>
      )}
    </Card>
  );
};

export default MonthSettingsCard;
